import {memo} from 'react'
import {Timeline, RelativeTime, LinkButton} from '@primer/react'
import type {TimelineItem} from '@github-ui/dotcom-schema/types/pull_requests/v1/timeline'
import {TimelineItemBadge} from './TimelineItemBadge'
import styles from './TimelineEvent.module.css'
import {TimelineAuthor} from './TimelineAuthor'
import {PullRequestReviewText} from './items/PullRequestReviewText'
import {PullRequestReviewAction} from './items/PullRequestReviewAction'
import {IssueEventText} from './items/IssueEventText'
import {PullRequestCommitText} from './items/PullRequestCommitText'
import {OtherItemText} from './items/OtherItemText'
import {IssueCommentAction} from './items/IssueCommentAction'

// narrow on the additionalInfo oneof so each leaf receives its proto info value
function ItemText({item}: {item: TimelineItem}) {
  const info = item.additionalInfo?.info
  switch (info?.case) {
    case 'pullRequestReview':
      return <PullRequestReviewText info={info.value} />
    case 'pullRequestCommit':
      return <PullRequestCommitText info={info.value} />
    case 'issueEvent':
      return <IssueEventText info={info.value} />
    default:
      return <OtherItemText item={item} />
  }
}

function ItemAction({item}: {item: TimelineItem}) {
  const info = item.additionalInfo?.info
  switch (info?.case) {
    case 'pullRequestReview':
      return <PullRequestReviewAction info={info.value} />
    case 'issueComment':
      return <IssueCommentAction info={info.value} />
    case 'issueEvent': {
      const sessionUrl = info.value.sessionUrl
      return sessionUrl ? (
        <LinkButton href={sessionUrl} size="small" variant="invisible">
          View session
        </LinkButton>
      ) : null
    }
    default:
      return null
  }
}

export const TimelineEvent = memo(function TimelineEventComponent({item}: {item: TimelineItem}) {
  return (
    <Timeline.Item condensed>
      <TimelineItemBadge item={item} />
      <Timeline.Body className="d-flex flex-row flex-justify-between flex-items-start">
        <span>
          <TimelineAuthor avatarUrl={item.user?.avatarUrl} login={item.user?.displayLogin} />
          <ItemText item={item} />
        </span>
        <span className="d-flex flex-row flex-items-center fgColor-muted">
          <ItemAction item={item} />
          <RelativeTime className={styles.timestamp} datetime={item.timestamp} format="micro" />
        </span>
      </Timeline.Body>
    </Timeline.Item>
  )
})
